import React, { useEffect, useState } from 'react';
import { ShoppingBag, Calendar, Wifi, WifiOff } from 'lucide-react';
import { getUserOrders } from '../../lib/supabase/stripe';
import { getProductByPriceId } from '../../stripe-config';
import type { OrderData } from '../../lib/supabase/stripe';

export default function SubscriptionStatus() {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionError, setConnectionError] = useState(false);

  useEffect(() => {
    async function fetchOrders() {
      try {
        setConnectionError(false);
        const data = await getUserOrders();
        setOrders(data);
      } catch (error) {
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
          setConnectionError(true);
        } else {
          console.error('Error fetching orders:', error);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchOrders();
  }, []);

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-200 h-16 rounded-lg"></div>
    );
  }

  if (connectionError) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-center">
          <WifiOff className="h-5 w-5 text-yellow-600 mr-2" />
          <div className="flex-1">
            <p className="text-sm text-yellow-800">Connection issue</p>
            <p className="text-xs text-yellow-600">Unable to load purchase history. Please check your connection.</p>
          </div>
        </div>
      </div>
    );
  }

  const recentOrders = orders.filter(order => 
    order.payment_status === 'paid' && order.order_status === 'completed'
  ).slice(0, 3);

  if (recentOrders.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-1">
            <p className="text-sm text-gray-600">No recent purchases</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-center mb-3">
        <ShoppingBag className="h-5 w-5 text-green-600 mr-2" />
        <h3 className="font-medium text-gray-900">Recent Purchases</h3>
      </div>
      
      <div className="space-y-2">
        {recentOrders.map((order) => {
          const product = getProductByPriceId(order.checkout_session_id); // This might need adjustment
          return (
            <div key={order.order_id} className="flex items-center justify-between text-sm">
              <div>
                <p className="font-medium text-gray-900">
                  Purchase #{order.order_id}
                </p>
                <p className="text-gray-600">
                  ${(order.amount_total / 100).toFixed(2)} - {order.payment_status}
                </p>
              </div>
              <div className="text-right">
                <div className="flex items-center text-gray-600">
                  <Calendar className="h-4 w-4 mr-1" />
                  <span>{new Date(order.order_date).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}